import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function YesNoField({ label, value, onChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-b border-gray-100">
      <p className="text-sm text-gray-900">{label}</p>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant={value === 'yes' ? 'default' : 'outline'}
          className={cn(value === 'yes' && 'bg-green-600 hover:bg-green-700')}
          onClick={() => onChange('yes')}>
          <CheckCircle className="h-3.5 w-3.5 mr-1" /> YES
        </Button>
        <Button type="button" size="sm" variant={value === 'no' ? 'default' : 'outline'}
          className={cn(value === 'no' && 'bg-red-600 hover:bg-red-700')}
          onClick={() => onChange('no')}>
          <XCircle className="h-3.5 w-3.5 mr-1" /> NO
        </Button>
      </div>
    </div>
  );
}

function buildInitialFormData(template) {
  const data = {};
  if (!template) return data;
  for (const field of template.header_fields || []) {
    data[field.id] = field.default || '';
  }
  for (const section of template.sections || []) {
    for (const field of section.fields || []) {
      if (field.type === 'development_plan_table') {
        const rowCount = field.row_count || 8;
        for (let i = 0; i < rowCount; i += 1) {
          for (const col of field.columns || []) {
            data[`${field.id}_${i}_${col.id}`] = '';
          }
        }
      } else {
        data[field.id] = '';
      }
    }
  }
  return data;
}

export default function StructuredAppraisalForm({
  open,
  onOpenChange,
  employeeId,
  token,
  mode = 'admin',
  cycleId = null,
  initialTemplateId = null,
  onSuccess,
}) {
  const [step, setStep] = useState('choose');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isWorker = mode === 'worker';

  useEffect(() => {
    if (!open) return;
    setStep(isWorker || initialTemplateId ? 'form' : 'choose');
    setSelectedTemplate(null);
    setFormData({});
    fetchTemplates();
  }, [open, mode, initialTemplateId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const url = isWorker ? `${API}/worker/appraisal-form-templates` : `${API}/appraisal-form-templates`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const loaded = response.data.templates || [];
      setTemplates(loaded);
      const pick = initialTemplateId
        ? loaded.find((t) => t.template_id === initialTemplateId)
        : isWorker
          ? loaded.find((t) => t.template_id === 'self_assessment')
          : null;
      if (pick) {
        setSelectedTemplate(pick);
        setFormData(buildInitialFormData(pick));
        setStep('form');
      }
    } catch (error) {
      toast.error('Failed to load appraisal forms');
    } finally {
      setLoading(false);
    }
  };

  const startTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(buildInitialFormData(template));
    setStep('form');
  };

  const updateField = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const sectionCount = useMemo(
    () => (selectedTemplate?.sections || []).length,
    [selectedTemplate],
  );

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    try {
      const payload = {
        template_id: selectedTemplate.template_id,
        form_data: formData,
        cycle_id: cycleId,
      };
      const url = isWorker
        ? `${API}/worker/appraisals/self-assessment`
        : `${API}/employees/${employeeId}/appraisals/structured`;
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${selectedTemplate.title} saved`);
      onOpenChange(false);
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors?.length) toast.error(detail.errors[0]);
      else if (typeof detail === 'string') toast.error(detail);
      else toast.error('Failed to save appraisal form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    if (field.type === 'yes_no') {
      return (
        <YesNoField
          key={field.id}
          label={field.label}
          value={formData[field.id]}
          onChange={(v) => updateField(field.id, v)}
        />
      );
    }
    if (field.type === 'development_plan_table') {
      const rowCount = field.row_count || 8;
      return (
        <div key={field.id} className="space-y-2 overflow-x-auto">
          <Label className="text-sm font-medium">{field.label}</Label>
          <table className="w-full text-xs border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="border p-2 w-8">#</th>
                {(field.columns || []).map((col) => (
                  <th key={col.id} className="border p-2 text-left">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }).map((_, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="border p-2 text-center text-gray-500">{rowIdx + 1}</td>
                  {(field.columns || []).map((col) => (
                    <td key={col.id} className="border p-1">
                      <Input
                        value={formData[`${field.id}_${rowIdx}_${col.id}`] || ''}
                        onChange={(e) => updateField(`${field.id}_${rowIdx}_${col.id}`, e.target.value)}
                        className="h-8 text-xs border-0 shadow-none"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div key={field.id} className="space-y-2">
          <Label className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Textarea rows={4} value={formData[field.id] || ''} onChange={(e) => updateField(field.id, e.target.value)} />
        </div>
      );
    }
    return (
      <div key={field.id} className="space-y-2">
        <Label className="text-sm font-medium">
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Input
          type={field.type === 'date' ? 'date' : 'text'}
          value={formData[field.id] || ''}
          onChange={(e) => updateField(field.id, e.target.value)}
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] max-h-[90vh] !flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            {step === 'choose' ? 'Appraisal forms' : selectedTemplate?.title}
          </DialogTitle>
          <DialogDescription>
            {isWorker
              ? 'Complete every section before your appraisal meeting. Your manager will use this in the annual review.'
              : 'Structured appraisal forms mapped from the approved workspace templates.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' ? (
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {templates.map((tpl) => (
                  <button key={tpl.template_id} type="button" onClick={() => startTemplate(tpl)}
                    className="text-left p-4 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5">
                    <p className="font-semibold">{tpl.title}</p>
                    <Badge variant="outline" className="mt-2">{tpl.section_count} sections</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="shrink-0 px-6 py-2 border-b bg-slate-50 text-xs text-gray-600">
              <strong>{sectionCount}</strong> sections — scroll down ↓
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
              {selectedTemplate?.intro && (
                <p className="text-sm text-gray-600 bg-slate-50 border rounded-lg p-3">{selectedTemplate.intro}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
                {(selectedTemplate?.header_fields || []).map((field) => (
                  <div key={field.id}>
                    <Label className="text-xs text-gray-600">{field.label}{field.required && ' *'}</Label>
                    <Input type={field.type === 'date' ? 'date' : 'text'} value={formData[field.id] || ''}
                      onChange={(e) => updateField(field.id, e.target.value)} className="mt-1" />
                  </div>
                ))}
              </div>
              {(selectedTemplate?.sections || []).map((section) => (
                <div key={section.id} className="space-y-3 p-4 border rounded-lg bg-white">
                  <div>
                    <h3 className="font-semibold text-primary text-sm">{section.title}</h3>
                    {section.guidance && <p className="text-xs text-gray-500 mt-1">{section.guidance}</p>}
                  </div>
                  <div className="space-y-3">{(section.fields || []).map((field) => renderField(field))}</div>
                </div>
              ))}
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white">
              {!isWorker && (
                <Button variant="outline" onClick={() => setStep('choose')} disabled={submitting}>Back</Button>
              )}
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save {isWorker ? 'self-assessment' : '& generate PDF'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
